<template>
  <div class="p-5 mb-5">
    <div class="flex-grow-1 d-flex flex-column justify-content-center">
      <div class="flex-grow-0 flex-shrink-0 container">
        <div class="row justify-content-md-center">
          <div class="col col-lg-8">
            <div class="bg-white shadow rounded-4">
              <div class="p-5">
                <div class="container text-center">
                  <h1 class="fw-bold fs-2">Sign in to ComplianceClerk</h1>
                  <p>No password, no problem.</p>

                  <form @submit.prevent="onSubmit">
                    <div class="card-body">
                      <div class="form-floating mb-3">
                        <Field
                          name="email"
                          type="email"
                          v-slot="{ field, errorMessage, meta }"
                        >
                          <input
                            v-bind="field"
                            type="email"
                            class="form-control rounded-3"
                            :class="{
                              'is-invalid': meta.touched && errorMessage
                            }"
                            id="email"
                            :disabled="isLoading"
                            placeholder="Email address"
                            autocomplete="email"
                          />
                          <div
                            class="invalid-feedback"
                            v-if="meta.touched && errorMessage"
                          >
                            {{ errorMessage }}
                          </div>
                        </Field>
                        <label for="email">Email</label>
                      </div>
                    </div>

                    <div class="card-footer">
                      <button
                        type="submit"
                        class="btn btn-lg btn-primary w-100"
                        :disabled="isLoading || Object.keys(errors).length > 0"
                      >
                        <span
                          v-if="isLoading"
                          class="spinner-border spinner-border-sm me-2"
                          role="status"
                        ></span>
                        {{ isLoading ? 'Sending...' : 'Sign In With Email' }}
                      </button>
                    </div>
                  </form>

                  <div
                    v-if="error"
                    class="alert alert-danger mt-3"
                    role="alert"
                  >
                    {{ error }}
                  </div>

                  <div
                    class="alert alert-light mt-3 text-muted text-start"
                    role="alert"
                  >
                    <div class="d-flex">
                      <div class="flex-shrink-0">
                        <i class="fa-light fa-sparkles"></i>
                      </div>
                      <div class="flex-grow-1 ms-2 fs-5">
                        We'll email you a magic code for a password-free sign
                        in. Or you can
                        <strong
                          ><a href="/accounts/login/"
                            >sign in manually instead.</a
                          ></strong
                        >
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useUserStore } from '../../stores/userStore'
import { useForm, Field } from 'vee-validate'
import * as yup from 'yup'

const userStore = useUserStore()

const isLoading = ref(false)
const error = ref('')

const emit = defineEmits(['code-requested'])

const schema = yup.object({
  email: yup
    .string()
    .required('Email is required')
    .email('Please enter a valid email address')
    .trim()
})

const { handleSubmit, errors } = useForm({
  validationSchema: schema,
  initialValues: {
    email: ''
  }
})

const onSubmit = handleSubmit(async (values) => {
  if (isLoading.value) return

  error.value = ''
  isLoading.value = true

  try {
    const success = await userStore.requestLoginCode(values.email)

    if (success) {
      emit('code-requested', values.email)
    } else {
      error.value = 'Failed to send login code. Please try again.'
    }
  } catch (e) {
    error.value = 'An unexpected error occurred. Please try again.'
    console.error('Login code request error:', e)
  } finally {
    isLoading.value = false
  }
})
</script>
